<template>
  <div class="location-link-tree">
    <div
      v-for="country in countriesView"
      :key="country.uuid"
    >
      <div v-if="shouldDisplayCountryLocationTree(country)">
        <p class="location-link-tree__country-label">
          {{ country.label }}
        </p>

        <SplNuxtLink
          v-if="generalParentLocation && generalParentLocation.type !== 'country'"
          :to="addBaseURLToSlug(generalParentLocation)"
          :query="linkQuery"
          is-external
        >
          <div class="location-link-tree__parent-link">
            <ArrowIcon
              class="location-link-tree__parent-link__icon"
              direction="left"
              variant="semidark"
              :size="18"
            />
            <div class="location-link-tree__parent-link__label">
              {{ generalParentLocation.label }}
            </div>
          </div>
        </SplNuxtLink>

        <TruncatedList
          :split-at="splitAtIndex"
          :data="getLocationsByCountry(country)"
          expand-button-left
        >
          <template #default="{ element }">
            <div
              class="location-link-tree__link"
              :class="{'current': element.current}"
              @click="toggleExpanded(element.uuid)"
            >
              <div class="location-link-tree__link__icon">
                <ArrowIcon
                  v-if="element.childLocations.length"
                  :direction="element.expanded ? 'bottom' : 'top'"
                  variant="semidark"
                  :size="18"
                />

                <ArrowIcon
                  v-else
                  :size="18"
                  variant="light"
                  direction="bottom"
                />
              </div>

              <SplNuxtLink
                class="location-link-tree__link__anchor"
                :to="addBaseURLToSlug(element.location)"
                :query="linkQuery"
                is-external
                :is-disabled="isCurrentLocation(element.location)"
              >
                {{ element.location.label }}
              </SplNuxtLink>
            </div>

            <div v-if="element.expanded">
              <div
                v-for="child in element.childLocations"
                :key="child.location.uuid"
                class="location-link-tree__sub-link"
              >
                <SplNuxtLink
                  :to="addBaseURLToSlug(child.location)"
                  :query="linkQuery"
                  is-external
                >
                  {{ child.location.label }}
                </SplNuxtLink>
              </div>
            </div>
          </template>
        </TruncatedList>
      </div>

      <div v-else>
        <div
          class="location-link-tree__swap-country-link"
          @click="storeCountryInSession(country)"
        >
          <DynLang
            by-key="go-to-XX"
            :params="{place: country.label}"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SplNuxtLink from '../shared/SplNuxtLink.vue'
import TruncatedList from '../shared/TruncatedList.vue'
import ArrowIcon from '../shared/icons/Arrow.icon.vue'

import { joinPaths, slashUri } from '~/core/ts/util/string'

import type { Location, LocationTree } from '~/core/ts/entity/Location'
import { useSessionLocation } from '~/core/composable/shared/useSessionLocation'

type LocationTreeFilterNode = LocationTree & {
  uuid: string;
  expanded: boolean;
  current: boolean;
}

export default defineNuxtComponent({
  components: {
    SplNuxtLink,
    TruncatedList,
    ArrowIcon,
  },
  props: {
    generalParentLocation: {
      type: Object as PropType<Location | undefined | null>,
      default: null,
    },
    locationsTree: {
      type: Object as PropType<LocationTree[]>,
      default: () => [],
    },
    countries: {
      type: Object as PropType<Array<Location>>,
      required: true,
    },
    currentLocation: {
      type: Object as PropType<Location | undefined | null>,
      default: null,
    },
    linkUrlBase: {
      type: String,
      required: true
    },
    linkQuery: {
      type: Object,
      default: () => ({}),
    },
    shouldSplit: {
      type: Boolean,
      default: false,
    },
    startsCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const sessionLocation = useSessionLocation()

    const locationDict: Record<string, Location> = {}
    function locationTreeToDict(tree: LocationTree[]) {
      tree.forEach((loc) => {
        locationDict[loc.location.uuid] = loc.location

        if (loc.childLocations.length) {
          locationTreeToDict(loc.childLocations)
        }
      })
    }

    function isCurrentLocation(loc: Location): boolean {
      if (!props.currentLocation) {
        return false
      }

      return props.currentLocation.uuid === loc.uuid
    }

    function addBaseURLToSlug(loc: Location) {
      if (loc.type === 'region' || loc.type === 'community') {
        const uri = joinPaths(props.linkUrlBase, loc.slug)
        return slashUri(uri)
      }

      const parentLoc = locationDict[loc.parentUUID || '']
      if (parentLoc) {
        const parentUri = joinPaths(props.linkUrlBase, parentLoc.slug)
        const uri = joinPaths(parentUri, loc.slug)
        return slashUri(uri)
      }

      const uri = joinPaths(props.linkUrlBase, loc.slug)
      return slashUri(uri)
    }

    locationTreeToDict(props.locationsTree)
    if (props.generalParentLocation) {
      locationDict[props.generalParentLocation.uuid] = props.generalParentLocation
    }

    let splitAtIndex = props.shouldSplit ? 5 : 9999
    const nodes = props.locationsTree
      .map((tree) => {
        const isCurrent = props.currentLocation ?
          tree.location.uuid === props.currentLocation.uuid :
          false

        return {
          ...tree,
          uuid: tree.location.uuid,
          expanded: props.startsCollapsed ? false : isCurrent,
          current: isCurrent,
        } as LocationTreeFilterNode
      })

    // Aparentemente no se puede usar sort porque causa un HYDRATION MISMATCH
    const current = nodes.filter((node) => node.current)
    const others = nodes.filter((node) => !node.current)
    const filterLocationsTree = ref([...current, ...others])

    function toggleExpanded(uuid: string) {
      filterLocationsTree.value = filterLocationsTree.value.map((node) => {
        if (node.uuid === uuid) {
          node.expanded = !node.expanded
        }

        return node
      })
    }

    function countryHasLocations(country: Location) {
      for (const node of filterLocationsTree.value) {
        if (node.location.countryCode === country.countryCode) {
          return true
        }
      }

      return false
    }

    function getLocationsByCountry(country: Location) {
      return filterLocationsTree.value.filter((node) => {
        return node.location.countryCode === country.countryCode
      })
    }

    function shouldDisplayCountryLocationTree(country: Location) {
      const stored = sessionLocation.storedLocationCountry.value
      if (stored) {
        return country.countryCode === stored.countryCode
      }

      return true
    }

    function storeCountryInSession(country: Location) {
      sessionLocation.storeCountryInSession(country)
    }

    const countriesView = computed(() => {
      let view = props.countries
        .filter((country) => countryHasLocations(country))

      const stored = sessionLocation.storedLocationCountry.value
      if (stored) {
        view = view
          .filter((loc) => loc.countryCode === stored.countryCode)
          .concat(
            view.filter((loc) => loc.countryCode !== stored.countryCode)
          )
      }

      return view
    })

    return {
      isCurrentLocation,
      addBaseURLToSlug,
      toggleExpanded,


      filterLocationsTree,

      countriesView,
      shouldDisplayCountryLocationTree,
      countryHasLocations,
      getLocationsByCountry,

      storeCountryInSession,

      splitAtIndex,
    }
  }
})
</script>

<style lang="scss" scoped>
.location-link-tree {
  @apply flex;
  @apply flex-col;

  @apply gap-4;

  &__country-label {
    @apply my-2;
    @apply font-semibold;
  }

  &__swap-country-link {
    @apply my-1;
    @apply font-semibold;
  }

  &__swap-country-link:hover {
    @apply underline;
    @apply cursor-pointer;
  }

  &__parent-link {
    @apply flex;
    @apply items-center;
    @apply font-bold;

    @apply py-1;
    @apply mb-2;

    &__icon {
      @apply pr-2;
    }

    &__label {
      @apply py-1;
    }
  }

  &__link {
    @apply text-spl-dark;
    @apply ml-1;
    @apply flex;
    @apply items-center;

    &__icon {
      @apply px-2;
      @screen lg {
        @apply px-1;
      }
    }

    &__anchor {
      @apply ml-2;
      @apply cursor-pointer;
    }
  }

  &__parent-link:hover, &__link:hover, &__sub-link:hover {
    @apply underline;
  }

  &__sub-link {
    @apply text-spl-dark;
    @apply ml-12;
    @apply pl-2 py-1;
  }
}

.current {
  @apply font-bold;
  @apply underline;
}
</style>
